/**
 * 店舗タブ用の API。
 *   GET /dashboard/store-summary?month=M       … 管理者ロールだけ（stylist は 403）。見える店舗を常に配列で（個人名なし）
 *   GET /dashboard/admin/store-buckets?month=M … 管理者ロールだけ。店舗ごとの人数（できてる/もう少し/要フォロー/目標未設定）
 *   GET /dashboard/forecasts?store=X           … 店舗合計の予測の行（forecast_people 付き）が、個人の行と一緒に返る
 * 店舗単位の推移は、既存の fetchKPIHistory({ store })（stylist 指定なし）を使う。
 *
 * サーバー側が会社 × 月で 2 分キャッシュしている。こちらも同じ長さで持ち、ログアウト時に消す
 * （別の人のデータが見えないように）。
 */
import { apiFetch } from './authApi';
import { getApiBaseUrl } from './apiBase';

const API_BASE_URL = getApiBaseUrl();
const TTL_MS = 2 * 60 * 1000;

const cache = new Map(); // key → { ts, promise }

async function getJson(url) {
  const response = await apiFetch(url);
  if (!response.ok) {
    const err = await response.json().catch(() => ({}));
    throw new Error(err.detail || `${response.status} ${response.statusText}`);
  }
  return response.json();
}

/** 同じ月の呼び出しは、TTL の間、1 回の取得を共有する。失敗はキャッシュしない。 */
function cached(key, load) {
  const hit = cache.get(key);
  if (hit && Date.now() - hit.ts < TTL_MS) return hit.promise;
  const promise = load().catch((err) => {
    cache.delete(key);
    throw err;
  });
  cache.set(key, { ts: Date.now(), promise });
  return promise;
}

export function fetchStoreSummary(month) {
  return cached(`summary|${month}`, () =>
    getJson(`${API_BASE_URL}/dashboard/store-summary?month=${encodeURIComponent(month)}`)
  );
}

export function fetchStoreBuckets(month) {
  return cached(`buckets|${month}`, () =>
    getJson(`${API_BASE_URL}/dashboard/admin/store-buckets?month=${encodeURIComponent(month)}`)
  );
}

/** 店舗合計の予測（stylist 指定なし）。個人の行も返るので、使う側が forecast_people のある行だけを選ぶ。 */
export function fetchStoreForecasts(store) {
  return cached(`forecasts|${store}`, () =>
    getJson(`${API_BASE_URL}/dashboard/forecasts?store=${encodeURIComponent(store)}`)
  );
}

/** ログアウト時（dashboardApi.clearDashboardCache から）に呼ぶ。 */
export function clearStoresCache() {
  cache.clear();
}
